import { useCallback, useEffect, useReducer, useRef } from 'react';
import { fetchSkinGachaBanner, HuntApiError, newIdempotencyKey, rollSkinGacha } from '../api/hunt';
import { SKIN_GACHA_INITIAL, type SkinGachaIntent, skinGachaReduce } from './skin-gacha-machine';

/**
 * The roulette's machine, wired to the real transport (gate V24C).
 *
 * ## What this adds to the reducer, and what it must not
 *
 * It owns the requests, the idempotency key and the classification of a
 * failure. It owns no rule about which phase follows which - that is the
 * reducer's, which is pure and proved on its own. A hook that decided both
 * would be a hook whose state machine could only be tested through a DOM.
 *
 * ## A double click makes one charge, before React re-renders
 *
 * Two guards, and both are needed. The reducer refuses a second `submit`
 * outside `confirming`, which is the authoritative one; and `inFlight` is a
 * REF, so the second click of a double click reads `true` synchronously rather
 * than reading state React has not re-rendered yet. The store panel documents
 * the same pair for the same reason.
 *
 * ## The key belongs to the INTENTION
 *
 * Minted when a player confirms. Kept while that attempt is retried, so a
 * dropped connection is repeated as a replay - the same copy, the same
 * capacity, one charge. Dropped when the attempt settles, so the next
 * confirmation is a new purchase. Both halves are the reducer's, and this hook
 * only ever reads the key it is holding.
 */

export interface SkinGachaOptions {
  /**
   * Called with the WHOLE confirmed answer, after a 200 the schema validated.
   *
   * The response and not the instance id: the id is what this tab asked for,
   * the answer is what the server did. Never called on a refusal, a conflict or
   * a transport failure - there is no confirmed state to hand anybody.
   */
  readonly onRolled?: (instanceId: string) => void;
}

/** A refusal, in the player's language. Never a status code on screen. */
function messageFor(error: unknown): string {
  if (error instanceof HuntApiError) {
    if (error.reason === 'insufficient_funds') {
      return 'Seu saldo de Cash não cobre esta rolagem.';
    }
    if (error.reason === 'banner_changed' || error.reason === 'banner_not_active') {
      return 'A oferta mudou enquanto você decidia. Recarregamos os dados — confira o preço e as chances antes de tentar de novo.';
    }
    if (error.reason === 'idempotency_conflict') {
      return 'Esta tentativa foi enviada com dados diferentes. Comece uma nova rolagem.';
    }
    if (error.status === 401 || error.status === 403) {
      return 'Sua sessão expirou. Entre novamente para rolar.';
    }
    if (error.status === 404) {
      return 'Esta oferta não está mais disponível.';
    }
  }
  return 'Não foi possível concluir a rolagem agora.';
}

/**
 * Whether a failure leaves the outcome UNKNOWN.
 *
 * The distinction that decides whether the same key may be repeated. A refusal
 * the server sent is definite: it arrived, so nothing was committed behind it.
 * A dropped connection or a 5xx is not: the transaction may have committed and
 * the answer may have been lost on the way back, so the only safe next move is
 * to repeat the SAME intention and let the envelope answer.
 */
function isUncertain(error: unknown): boolean {
  if (error instanceof HuntApiError) {
    return error.status >= 500;
  }
  // A `TypeError` from `fetch`: the request may or may not have been served.
  return true;
}

export function useSkinGacha(options: SkinGachaOptions = {}) {
  const [state, dispatch] = useReducer(skinGachaReduce, SKIN_GACHA_INITIAL);
  /** Guards a double click even before React has re-rendered the button. */
  const inFlight = useRef(false);
  /** True while mounted. Nothing dispatches into an unmounted tree. */
  const alive = useRef(true);
  const rolled = useRef(options.onRolled);
  rolled.current = options.onRolled;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    dispatch({ type: 'reload' });
    try {
      const banner = await fetchSkinGachaBanner();
      if (alive.current) {
        dispatch({ type: 'banner_available', banner });
      }
    } catch (error) {
      if (!alive.current) {
        return;
      }
      /*
       * A 404 is the ANSWER, not an outage.
       *
       * Every shipped build answers `banner_not_available`, because no banner is
       * active anywhere. Rendering that as an error would put a red box on a
       * screen that is behaving exactly as designed.
       */
      if (error instanceof HuntApiError && error.status === 404) {
        dispatch({ type: 'banner_unavailable' });
        return;
      }
      dispatch({
        type: 'banner_failed',
        message: 'Não foi possível ler a oferta agora. Tente novamente em instantes.',
      });
    }
  }, []);

  /** Reads once, when the screen that owns this hook mounts. */
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const send = useCallback(
    async (intent: SkinGachaIntent) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      try {
        const result = await rollSkinGacha({
          bannerId: intent.bannerId,
          bannerVersion: intent.bannerVersion,
          bannerFingerprint: intent.bannerFingerprint,
          idempotencyKey: intent.idempotencyKey,
        });
        if (!alive.current) {
          return;
        }
        dispatch({ type: 'rolled', result });
        /*
         * The shelf is told to re-read, with the server's own answer in hand.
         *
         * After the dispatch, so the reveal and the wardrobe can never be given
         * two different pictures in two different orders; and inside the success
         * path, so a refusal or a dropped connection reaches nobody.
         */
        rolled.current?.(result.itemInstanceId);
        // The balance moved. Re-read rather than predict - and the reducer
        // refuses to let this walk over the reveal in progress.
        void refresh();
      } catch (error) {
        if (!alive.current) {
          return;
        }
        const message = messageFor(error);
        if (error instanceof HuntApiError && error.reason === 'insufficient_funds') {
          dispatch({ type: 'insufficient', message });
          void refresh();
          return;
        }
        if (isUncertain(error)) {
          dispatch({
            type: 'uncertain',
            message:
              'A resposta não chegou. Tente novamente: a mesma tentativa é reenviada e você não será cobrado duas vezes.',
          });
          return;
        }
        dispatch({ type: 'rejected', message });
        void refresh();
      } finally {
        inFlight.current = false;
      }
    },
    [refresh],
  );

  /** Confirms the purchase. Mints ONE key for this intention. */
  const confirm = useCallback(() => {
    const banner = state.banner;
    if (!banner || state.phase !== 'confirming' || inFlight.current) {
      return;
    }
    const intent: SkinGachaIntent = {
      idempotencyKey: newIdempotencyKey('skin-gacha'),
      bannerId: banner.bannerId,
      bannerVersion: banner.bannerVersion,
      bannerFingerprint: banner.fingerprint,
    };
    dispatch({ type: 'submit', intent });
    void send(intent);
  }, [send, state.banner, state.phase]);

  /** Repeats an attempt whose answer was lost, under the SAME key. */
  const retry = useCallback(() => {
    if (state.phase !== 'uncertain' || !state.intent || inFlight.current) {
      return;
    }
    const intent = state.intent;
    dispatch({ type: 'retry' });
    void send(intent);
  }, [send, state.intent, state.phase]);

  return {
    state,
    refresh,
    confirm,
    retry,
    openDetails: useCallback(() => dispatch({ type: 'open_details' }), []),
    closeDetails: useCallback(() => dispatch({ type: 'close_details' }), []),
    requestConfirmation: useCallback(() => dispatch({ type: 'request_confirmation' }), []),
    cancelConfirmation: useCallback(() => dispatch({ type: 'cancel_confirmation' }), []),
    revealFinished: useCallback(() => dispatch({ type: 'reveal_finished' }), []),
    dismiss: useCallback(() => dispatch({ type: 'dismiss' }), []),
  };
}
